import apiClient from '../client';
import type {
  CompanyInfoResponse,
  FinancialPeriodInfo,
  BusinessClassInfo,
  CurrencyOption,
  LanguageOption,
  IFRS17PolicyResponse,
  IFRS17PolicyUpdate,
  IFRS17PolicyOptions,
  IFRS17PortfolioPolicyResponse,
  IFRS17PortfolioPolicyUpdate,
} from '../../types';
import type {
  IFRS4BalanceSheetEntry,
  IFRS4BSEntryCreate,
  IFRS4LineItemDef,
  IFRS4AnalysisResponse,
} from '../../types/ifrs4';

// ─── Company Information Endpoints ──────────────────────────────────────────

export const company = {
  getInfo: async (): Promise<CompanyInfoResponse> => {
    const response = await apiClient.get<CompanyInfoResponse>('/company');
    return response.data;
  },

  toggleBusinessClass: async (classCode: string): Promise<BusinessClassInfo> => {
    const response = await apiClient.put<BusinessClassInfo>(
      `/company/business-classes/${encodeURIComponent(classCode)}/toggle`
    );
    return response.data;
  },

  getCurrencies: async (): Promise<CurrencyOption[]> => {
    const response = await apiClient.get<CurrencyOption[]>('/company/currencies');
    return response.data;
  },

  updateCurrency: async (data: {
    currency_code: string | null;
    currency_name: string | null;
    currency_symbol: string | null;
  }): Promise<{ message: string; currency_code: string | null }> => {
    const response = await apiClient.put('/company/currency', data);
    return response.data;
  },

  updatePeriodCurrency: async (
    year: number,
    data: {
      currency_code: string | null;
      currency_name: string | null;
      currency_symbol: string | null;
    }
  ): Promise<{ message: string; year: number; currency_code: string | null }> => {
    const response = await apiClient.put(`/company/periods/${year}/currency`, data);
    return response.data;
  },

  getLanguages: async (): Promise<LanguageOption[]> => {
    const response = await apiClient.get<LanguageOption[]>('/company/languages');
    return response.data;
  },

  updateLanguage: async (data: {
    language_code: string | null;
  }): Promise<{ message: string; language_code: string | null }> => {
    const response = await apiClient.put('/company/language', data);
    return response.data;
  },

  createPeriod: async (data: {
    year: number;
    is_transition?: boolean;
  }): Promise<FinancialPeriodInfo> => {
    const response = await apiClient.post<FinancialPeriodInfo>('/company/periods', data);
    return response.data;
  },

  deletePeriod: async (year: number): Promise<{ message: string; year: number }> => {
    const response = await apiClient.delete(`/company/periods/${year}`);
    return response.data;
  },
};

// ─── IFRS 17 Policies Endpoints ──────────────────────────────────────────────

export const ifrs17Policies = {
  getAll: async (): Promise<IFRS17PolicyResponse[]> => {
    const response = await apiClient.get<IFRS17PolicyResponse[]>('/ifrs17-policies');
    return response.data;
  },

  getByYear: async (year: number): Promise<IFRS17PolicyResponse> => {
    const response = await apiClient.get<IFRS17PolicyResponse>(`/ifrs17-policies/${year}`);
    return response.data;
  },

  getOptions: async (): Promise<IFRS17PolicyOptions> => {
    const response = await apiClient.get<IFRS17PolicyOptions>('/ifrs17-policies/options');
    return response.data;
  },

  update: async (year: number, data: IFRS17PolicyUpdate): Promise<IFRS17PolicyResponse> => {
    const response = await apiClient.put<IFRS17PolicyResponse>(
      `/ifrs17-policies/${year}`,
      data
    );
    return response.data;
  },

  initialize: async (): Promise<IFRS17PolicyResponse[]> => {
    const response = await apiClient.post<IFRS17PolicyResponse[]>('/ifrs17-policies/initialize');
    return response.data;
  },

  // ── Per-portfolio endpoints ─────────────────────────────────────────────

  getPortfolios: async (year: number): Promise<IFRS17PortfolioPolicyResponse[]> => {
    const response = await apiClient.get<IFRS17PortfolioPolicyResponse[]>(
      `/ifrs17-policies/${year}/portfolios`
    );
    return response.data;
  },

  getPortfolio: async (year: number, businessClass: string): Promise<IFRS17PortfolioPolicyResponse> => {
    const response = await apiClient.get<IFRS17PortfolioPolicyResponse>(
      `/ifrs17-policies/${year}/portfolios/${encodeURIComponent(businessClass)}`
    );
    return response.data;
  },

  updatePortfolio: async (
    year: number,
    businessClass: string,
    data: IFRS17PortfolioPolicyUpdate,
  ): Promise<IFRS17PortfolioPolicyResponse> => {
    const response = await apiClient.put<IFRS17PortfolioPolicyResponse>(
      `/ifrs17-policies/${year}/portfolios/${encodeURIComponent(businessClass)}`,
      data
    );
    return response.data;
  },
};

// ─── IFRS 4 Balance Sheet Endpoints ─────────────────────────────────────────

export const ifrs4BalanceSheet = {
  getAll: async (year?: number): Promise<IFRS4BalanceSheetEntry[]> => {
    const params: Record<string, number> = {};
    if (year) params.year = year;
    const response = await apiClient.get<IFRS4BalanceSheetEntry[]>(
      '/data-entry/ifrs4-balance-sheet',
      { params },
    );
    return response.data;
  },

  getLineItems: async (includeLt?: boolean): Promise<IFRS4LineItemDef[]> => {
    const params = includeLt ? { include_lt: true } : {};
    const response = await apiClient.get<IFRS4LineItemDef[]>(
      '/data-entry/ifrs4-balance-sheet/line-items',
      { params },
    );
    return response.data;
  },

  upsert: async (data: IFRS4BSEntryCreate): Promise<IFRS4BalanceSheetEntry> => {
    const response = await apiClient.post<IFRS4BalanceSheetEntry>(
      '/data-entry/ifrs4-balance-sheet',
      data,
    );
    return response.data;
  },

  bulkUpsert: async (entries: IFRS4BSEntryCreate[]): Promise<IFRS4BalanceSheetEntry[]> => {
    const response = await apiClient.post<IFRS4BalanceSheetEntry[]>(
      '/data-entry/ifrs4-balance-sheet/bulk',
      { entries },
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/data-entry/ifrs4-balance-sheet/${id}`);
  },
};

// ─── IFRS 4 Conversion Analysis Endpoints ───────────────────────────────────

export const ifrs4Conversion = {
  getAnalysis: async (year: number): Promise<IFRS4AnalysisResponse> => {
    const response = await apiClient.get<IFRS4AnalysisResponse>(
      '/ifrs4-conversion/analysis',
      { params: { year } },
    );
    return response.data;
  },
};
